import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/system';
import { makeStyles } from '@mui/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { uploadFile, removeFile } from '../../../../features/fileUpload/fileUpload.slice';
import { ButtonBase, LinearProgress, Link, Tooltip, Typography } from '@mui/material';
import { Fragment } from 'react';
import { formatBytes } from '../../../../libs/helpers';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';
import { Report } from '../../../../libs/loggers';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    border: 'dashed 2px',
    borderColor: theme.palette.primary.main,
  },
  uploadedHeader: {
    color: theme.palette.primary.main,
    fontSize: '1em',
  },
}));

function FileDropzone({ defaultValue, category, ...props }) {
  const { name, label, tooltip, info, passwordField } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const uploadedProgress = useSelector((state) => state.fileUpload.file?.progress);

  const userId = useSelector((state) => state.submition.data?.userId);
  const submitionId = useSelector((state) => state.submition.submition?.submitionId);

  let page = window.location.pathname.replace(/^\/|\/$/g, '');
  const stepper = useSelector((state) => state.stepper.value);
  const fileLoading = useSelector((state) => state.fileUpload.file?.loading);
  const [file, setFile] = useState();
  const [droppedFile, setDroppedFile] = useState();
  const {
    register,
    unregister,
    setValue,
    getValues,
    resetField,
    formState: { errors },
  } = useFormContext();

  const onDrop = useCallback(
    async (droppedFile) => {
      setDroppedFile(droppedFile[0]);
      const formData = new FormData();
      formData.append('file', droppedFile[0]);
      formData.append('category', category);
      await dispatch(uploadFile(formData))
        .unwrap()
        .then((result) => {
          if (result.success) {
            const eventInfo = {
              action: 'upload',
              category: 'file',
              description: 'uploading a file',
              value: {
                currentPage: page,
                step: stepper.active + 1,
                fileName: result.upload.fileName,
                url: result.upload.location,
                value: 'file_upload',
              },
            };
            Report(eventInfo, userId, submitionId);
            const current = getValues(name) || {};
            setValue(
              name,
              { ...result.upload, password: current.password || '' },
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            );
            setFile(result.upload);
          }
        });
    },
    [setValue, name]
  );

  const handleRemove = (file) => {
    dispatch(removeFile({ fileId: file }))
      .unwrap()
      .then((result) => {
        if (result.success) {
          setDroppedFile(null);
          setFile(null);

          resetField(name, {
            defaultValue: undefined,
            keepDirty: true,
            keepError: false,
          });

          setValue(name, undefined, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: props.accept,
  });

  useEffect(() => {
    register(name);

    return () => {
      unregister(name);
    };
  }, [register, unregister, name]);

  useEffect(() => {
    if (defaultValue) {
      setValue(name, defaultValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setFile(defaultValue);
    }
  }, [defaultValue]);

  return (
    <Fragment>
      <Typography sx={{ mt: 3 }} variant={'h5'}>
        {label}{' '}
        {tooltip && (
          <Tooltip
            leaveTouchDelay={2500}
            enterTouchDelay={0}
            title={<Typography>{tooltip}</Typography>}
          >
            <IconButton sx={{ padding: 0 }}>
              <InfoIcon color="primary" size={'small'} />
            </IconButton>
          </Tooltip>
        )}
      </Typography>

      {info && <FormHelperText sx={{ mt: 0 }}>{info}</FormHelperText>}
      <Box
        {...getRootProps()}
        className={classes.root}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        id={name}
      >
        {droppedFile && droppedFile.path ? (
          <Fragment>
            {!fileLoading ? (
              <>
                <Typography className={classes.uploadedHeader}>Uploaded Files</Typography>
                <Typography>
                  {droppedFile.path} - {formatBytes(droppedFile.size)}{' '}
                  <ButtonBase onClick={() => handleRemove(file?._id)}>
                    <Link>Remove</Link>
                  </ButtonBase>
                </Typography>
              </>
            ) : (
              <>
                <Typography className={classes.uploadedHeader}>Uploading File</Typography>
                {droppedFile.path} - {formatBytes(droppedFile.size)}{' '}
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadedProgress || 0}
                    color={'primary'}
                  />
                </Box>
              </>
            )}
          </Fragment>
        ) : file && file._id ? (
          <>
            <Typography className={classes.uploadedHeader}>Uploaded File</Typography>
            <Typography>
              {file.fileName}{' '}
              <ButtonBase onClick={() => handleRemove(file?._id)}>
                <Link>Remove</Link>
              </ButtonBase>
            </Typography>
          </>
        ) : (
          <>
            <input {...props} {...getInputProps({ multiple: false })} />
            <CloudUploadIcon fontSize="large" />
            {isDragActive ? (
              <Typography>Drop the files here ...</Typography>
            ) : (
              <Typography>Drag 'n' drop files here, or click to select files</Typography>
            )}
          </>
        )}
        {passwordField && (
          <Box mt={2} width="100%">
            {passwordField}
          </Box>
        )}
        {errors[name] && (
          <Typography sx={{ pt: 2 }} color={'error'}>
            {errors[name].message}
          </Typography>
        )}
      </Box>
    </Fragment>
  );
}

export default FileDropzone;
